import React, { useState } from 'react'
import { Collapse } from '@mui/material'

import { Chip, Icon, type IconProps, Typography } from '~components/internal'
import {
  type AppliedUsagePolicies,
  type EnrichedOrganizationAISettings,
  type EnrichedToolCall,
} from '~types'

import { ToolCallInfo } from '../../components/ToolCallInfo'

import './AgentToolCallDrawer.scss'

export type AgentGroupItem = {
  agentLabel: 'Gatekeeper Agent' | 'Retrieval Agent' | 'Generator Agent'
  toolCalls: EnrichedToolCall[]
  model: string
  durationMs?: number
  policies?: AppliedUsagePolicies | null
}

const AGENT_ICONS: Record<AgentGroupItem['agentLabel'], IconProps['name']> = {
  'Gatekeeper Agent': 'security',
  'Retrieval Agent': 'manageSearch',
  'Generator Agent': 'autoAwesome',
}

const formatDuration = (ms: number): string => {
  if (ms < 1000) return `${ms}ms`
  return `${(ms / 1000).toFixed(2)}s`
}

interface AgentToolCallDrawerProps {
  group: AgentGroupItem
  orgAiSettings: EnrichedOrganizationAISettings | null
}

const AgentToolCallDrawer: React.FC<AgentToolCallDrawerProps> = ({ group, orgAiSettings }) => {
  const [isOpen, setIsOpen] = useState(false)
  const isRetrieval = group.agentLabel === 'Retrieval Agent'
  const policies = group.policies?.policies ?? []
  const isCollapsible = isRetrieval || policies.length > 0

  const headerContent = (
    <div className='agent-tool-call-drawer__header-left'>
      <Icon
        name={AGENT_ICONS[group.agentLabel]}
        className='agent-tool-call-drawer__agent-icon'
        fontSize='small'
      />
      <Typography variant='body2' className='agent-tool-call-drawer__label'>
        {group.agentLabel}
      </Typography>
      {isRetrieval && group.toolCalls.length > 0 && (
        <Chip
          label={`${group.toolCalls.length} tool${group.toolCalls.length !== 1 ? 's' : ''}`}
          size='small'
        />
      )}
      {group.model && <Chip label={group.model} size='small' />}
      {group.durationMs != null && (
        <Chip
          icon={<Icon name='accessTime' fontSize='small' />}
          label={formatDuration(group.durationMs)}
          size='small'
        />
      )}
    </div>
  )

  return (
    <div className='agent-tool-call-drawer'>
      <div className='agent-tool-call-drawer__card'>
        {isCollapsible ? (
          <>
            <button
              className='agent-tool-call-drawer__header'
              onClick={() => setIsOpen((prev) => !prev)}
              type='button'
            >
              {headerContent}
              <Icon
                name={isOpen ? 'expandLess' : 'expandMore'}
                className='agent-tool-call-drawer__chevron'
                fontSize='small'
              />
            </button>
            <Collapse in={isOpen}>
              <div className='agent-tool-call-drawer__body'>
                {group.toolCalls.map((toolCall) => (
                  <ToolCallInfo
                    key={toolCall.id}
                    toolCall={toolCall}
                    orgAiSettings={orgAiSettings}
                  />
                ))}
                {group.policies && policies.length > 0 && (
                  <div className='agent-tool-call-drawer__policies'>
                    <div className='agent-tool-call-drawer__policies-header'>
                      <Typography
                        variant='caption'
                        className='agent-tool-call-drawer__policies-heading'
                      >
                        {group.agentLabel === 'Gatekeeper Agent'
                          ? 'Inbound Policies'
                          : 'Outbound Policies'}
                      </Typography>
                      <Chip
                        label={`Version ${group.policies.snapshot_version}`}
                        variant='outlined'
                        size='small'
                        className='agent-tool-call-drawer__policies-version'
                      />
                    </div>
                    <div className='agent-tool-call-drawer__policy-rows'>
                      {policies.map((policy) => (
                        <div key={policy.name} className='agent-tool-call-drawer__policy-row'>
                          <Typography
                            variant='body2'
                            className='agent-tool-call-drawer__policy-name'
                          >
                            {policy.name}
                          </Typography>
                          <Chip
                            label={policy.action === 'BLOCK' ? 'Block' : 'Flag'}
                            color={policy.action === 'BLOCK' ? 'red' : 'orange'}
                            icon={
                              policy.action === 'BLOCK' ? (
                                <Icon name='block' size={14} fill={0} />
                              ) : (
                                <Icon name='flag' size={14} />
                              )
                            }
                            size='small'
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Collapse>
          </>
        ) : (
          <div className='agent-tool-call-drawer__header agent-tool-call-drawer__header--static'>
            {headerContent}
          </div>
        )}
      </div>
    </div>
  )
}

export default AgentToolCallDrawer
